import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Avatar from '@justgiving/avatar';
import { deleteIcon, inlineMenuIcon, refreshIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';

import { FitnessActivity } from '../../../../types/__generated__';
import { useBasePageQuery } from '../../../hooks';
import { useTimeline } from '../../../hooks/timeline/useTimeline';
import { makePhotoUrlFromAbsoluteUrl } from '../../../utils/makePhotoUrl';
import {
  DropdownMenu,
  DropdownMenuItem,
} from '../../DropdownMenu/DropdownMenu';
import { FormattedDate } from '../../Formatters/FormattedDate';
import IsPageOwner from '../../IsPageOwner/IsPageOwner';
import { StravaAvatarBadge } from '../StravaTimelineEntry/StravaAvatarBadge';

import timelineStyles from '../Timeline.scss';
import timelineEntryStyles from '../TimelineEntry/TimelineEntry.scss';

interface HeaderProps {
  id: string;
  fitnessActivity?: FitnessActivity | null;
  createdAt: string;
  onDeleteEntry: (id?: string) => void;
  refreshRequestedTime: string | Date | null;
  isStravaActivity: boolean;
}

export const Header: FunctionComponent<HeaderProps> = ({
  id,
  fitnessActivity,
  createdAt,
  isStravaActivity,
  refreshRequestedTime,
  onDeleteEntry = () => {},
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data } = useBasePageQuery();
  const { refreshTimelineEntry } = useTimeline();

  const shouldDisableRefreshButton = () => {
    if (!refreshRequestedTime) return false;

    const earliestTimeToEnableRefreshButton = new Date(refreshRequestedTime);

    earliestTimeToEnableRefreshButton.setSeconds(
      earliestTimeToEnableRefreshButton.getSeconds() + 1,
    );

    return new Date(Date.now()) < earliestTimeToEnableRefreshButton;
  };

  const [disableRefresh, setDisableRefresh] = useState(
    shouldDisableRefreshButton(),
  );

  const handleRefresh = async () => {
    await refreshTimelineEntry(id, () => setDisableRefresh(true));
  };

  const buildMenuItems = () => {
    const items: DropdownMenuItem[] = [
      {
        key: 'delete',
        icon: deleteIcon,
        label: fitnessActivity
          ? t('timeline.deleteFitnessTimelineEntry.label')
          : t('timeline.deleteNonFitnessTimelineEntry.label'),
        onClick: () => onDeleteEntry(id),
      },
    ];

    if (isStravaActivity) {
      items.push({
        key: 'refresh',
        icon: refreshIcon,
        label: disableRefresh
          ? t('timeline.refreshingTimelineEntry.label')
          : t('timeline.refreshTimelineEntry.label'),
        onClick: handleRefresh,
        disabled: disableRefresh,
      });
    }

    return items;
  };

  return (
    <div className={timelineStyles.header}>
      <div aria-label={t('timeline.avatar')} className={timelineStyles.avatar}>
        <Avatar
          avatarSize="large"
          picture={makePhotoUrlFromAbsoluteUrl(data?.page?.owner?.avatar)}
        />
        {isStravaActivity && (
          <span className={timelineEntryStyles.avatarBadge}>
            {<StravaAvatarBadge />}
          </span>
        )}
      </div>
      <div className="jg-display-flex jg-flex-g1 jg-flex-aic">
        <div className="jg-flex-g1">
          <h2 className={timelineStyles.title}>
            {isStravaActivity ? (
              <>
                <strong>{data?.page?.owner?.name}</strong> via{' '}
                <strong>Strava</strong>
              </>
            ) : (
              data?.page?.owner?.name
            )}
          </h2>
          <p className={timelineEntryStyles.subtitle}>
            {createdAt && <FormattedDate value={createdAt} />}
          </p>
        </div>
        <IsPageOwner>
          <DropdownMenu
            ariaLabel={t('timeline.menu')}
            items={buildMenuItems()}
            icon={<SVG markup={inlineMenuIcon} />}
          />
        </IsPageOwner>
      </div>
    </div>
  );
};

Header.displayName = 'TimelineEntryHeader';
